import React from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import { EditButton } from "@refinedev/mui";

import { DataGrid } from "@shared/components/DataGrid";
import { TableCell } from "@shared/components";
import {
  HistoryTimeline,
  RejectModal,
  SummaryCards,
} from "@modules/partners/components";
import { formatCurrency } from "@core/utils/masks";

import { usePartnerDetail } from "./controller";

export const PartnerDetail = React.memo(() => {
  const {
    id,
    tab,
    setTab,
    partner,
    isLoading,
    summary,
    referrals,
    monthly,
    history,
    rejectOpen,
    setRejectOpen,
    isRejecting,
    handleApprove,
    handleReject,
    translate,
  } = usePartnerDetail();

  if (isLoading || !partner) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  const referralColumns: GridColDef[] = [
    {
      field: "company_name",
      headerName: translate("v1/partner_referral_admin.fields.company_name"),
      minWidth: 200,
      renderCell: ({ value }) => <TableCell.Text value={value} textAlign="left" />,
    },
    {
      field: "sales_value",
      headerName: translate("v1/partner_referral_admin.fields.sales_value"),
      minWidth: 130,
      renderCell: ({ value }) => (
        <TableCell.Text value={formatCurrency(value)} />
      ),
    },
    {
      field: "commission_value",
      headerName: translate("v1/partner_referral_admin.fields.commission_value"),
      minWidth: 130,
      renderCell: ({ value }) => (
        <TableCell.Text value={formatCurrency(value)} />
      ),
    },
    {
      field: "status",
      headerName: translate("v1/partner_referral_admin.fields.status"),
      minWidth: 120,
      renderCell: ({ value }) => (
        <TableCell.Text value={translate(`partners.status.${value}`)} />
      ),
    },
    {
      field: "created_at",
      headerName: translate("v1/partner_referral_admin.fields.created_at"),
      minWidth: 120,
      renderCell: ({ value }) => <TableCell.Date value={value} />,
    },
  ];

  const monthlyColumns: GridColDef[] = [
    {
      field: "year",
      headerName: translate("v1/partner_monthly_admin.fields.year"),
      minWidth: 90,
      renderCell: ({ value }) => <TableCell.Text value={value} />,
    },
    {
      field: "month",
      headerName: translate("v1/partner_monthly_admin.fields.month"),
      minWidth: 90,
      renderCell: ({ value }) => <TableCell.Text value={value} />,
    },
    {
      field: "total_mrr",
      headerName: translate("v1/partner_monthly_admin.fields.total_mrr"),
      minWidth: 130,
      renderCell: ({ value }) => (
        <TableCell.Text value={formatCurrency(value)} />
      ),
    },
    {
      field: "total_commission",
      headerName: translate("v1/partner_monthly_admin.fields.total_commission"),
      minWidth: 130,
      renderCell: ({ value }) => (
        <TableCell.Text value={formatCurrency(value)} />
      ),
    },
    {
      field: "referrals_count",
      headerName: translate("v1/partner_monthly_admin.fields.referrals_count"),
      minWidth: 110,
      renderCell: ({ value }) => <TableCell.Text value={value} />,
    },
  ];

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
        <Typography variant="h5">{partner.name}</Typography>
        <Chip
          size="small"
          color={
            partner.status === "approved"
              ? "success"
              : partner.status === "rejected"
                ? "error"
                : "warning"
          }
          label={translate(`partners.status.${partner.status}`)}
        />
        <Box sx={{ flexGrow: 1 }} />
        {partner.status === "pending" && (
          <>
            <Button variant="contained" color="success" onClick={handleApprove}>
              {translate("partners.actions.approve")}
            </Button>
            <Button variant="outlined" color="error" onClick={() => setRejectOpen(true)}>
              {translate("partners.actions.reject")}
            </Button>
          </>
        )}
        <EditButton recordItemId={id} resource="v1/partner_admin" />
      </Box>

      {partner.rejection_reason && (
        <Typography color="error" sx={{ mb: 2 }}>
          {translate("partners.reject.reason")}: {partner.rejection_reason}
        </Typography>
      )}

      <Tabs value={tab} onChange={(_, value) => setTab(value)} sx={{ mb: 2 }}>
        <Tab value="summary" label={translate("partners.detail.summary")} />
        <Tab value="referrals" label={translate("partners.detail.referrals")} />
        <Tab value="monthly" label={translate("partners.detail.monthly")} />
        <Tab value="history" label={translate("partners.detail.history")} />
      </Tabs>
      <Divider sx={{ mb: 2 }} />

      {tab === "summary" && (
        <SummaryCards
          items={[
            {
              label: translate("v1/partner_admin.fields.tier"),
              value: String(partner.tier ?? "-"),
            },
            {
              label: translate("v1/partner_admin.fields.total_mrr"),
              value: formatCurrency(
                (summary?.total_mrr as string) ?? partner.total_mrr
              ),
            },
            {
              label: translate("v1/partner_admin.fields.total_commission"),
              value: formatCurrency(
                (summary?.total_commission as string) ?? partner.total_commission
              ),
            },
            {
              label: translate("v1/partner_admin.fields.effective_commission_percentage"),
              value: partner.effective_commission_percentage,
            },
            {
              label: translate("v1/partner_admin.fields.referrals_count"),
              value: (summary?.referrals_count as number) ?? partner.referrals_count,
            },
          ]}
        />
      )}

      {tab === "referrals" && (
        <DataGrid rows={referrals ?? []} columns={referralColumns} hideFooter />
      )}
      {tab === "monthly" && (
        <DataGrid
          rows={monthly ?? []}
          columns={monthlyColumns}
          hideFooter
          getRowId={(row) => row.id ?? `${row.year}-${row.month}`}
        />
      )}
      {tab === "history" && <HistoryTimeline entries={history} />}

      <RejectModal
        open={rejectOpen}
        loading={isRejecting}
        onClose={() => setRejectOpen(false)}
        onConfirm={handleReject}
      />
    </Box>
  );
});
